export type DegreeFilterReducerState = {
  selectedProductVariant?: string;
  selectedDomain?: string;
  selectedApplicationOpen?: string;
  selectedCost?: string;
  selectedDuration?: string;
  selectedOpenContentAvailability?: string;
  submittedProductVariant?: string;
  submittedDomain?: string;
  submittedApplicationOpen?: string;
  submittedCostBucket?: string;
  submittedDurationBucket?: string;
  submittedOpenContentAvailabilityBucket?: string;
  open: boolean;
  // Flips to true once the one-time localStorage hydration (HYDRATE_FROM_STORAGE) has run.
  // The consuming hook uses this to gate the "save filters to localStorage" effect so it
  // doesn't fire -- and overwrite the user's stored filters with the initial empty state --
  // before hydration has had a chance to apply them.
  startupComplete: boolean;
};

export type DegreeFilterHydrationPayload = {
  domain?: string;
  productVariant?: string;
  applicationOpen?: string;
  costBucket?: string;
  durationBucket?: string;
  openContentAvailabilityBucket?: string;
} | null;

export type DegreeFilterAction =
  | { type: 'SET_SELECTED_PRODUCT_VARIANT'; payload: string | undefined }
  | { type: 'SET_SELECTED_DOMAIN'; payload: string | undefined }
  | { type: 'SET_SELECTED_APPLICATION_OPEN'; payload: string | undefined }
  | { type: 'SET_SELECTED_COST'; payload: string | undefined }
  | { type: 'SET_SELECTED_DURATION'; payload: string | undefined }
  | { type: 'SET_SELECTED_OPEN_CONTENT_AVAILABILITY'; payload: string | undefined }
  | { type: 'SET_SUBMITTED_PRODUCT_VARIANT'; payload: string | undefined }
  | { type: 'SET_SUBMITTED_DOMAIN'; payload: string | undefined }
  | { type: 'SET_SUBMITTED_APPLICATION_OPEN'; payload: string | undefined }
  | { type: 'SET_SUBMITTED_COST_BUCKET'; payload: string | undefined }
  | { type: 'SET_SUBMITTED_DURATION_BUCKET'; payload: string | undefined }
  | { type: 'SET_SUBMITTED_OPEN_CONTENT_AVAILABILITY_BUCKET'; payload: string | undefined }
  | { type: 'SET_OPEN'; payload: boolean }
  | { type: 'CLEAR_ALL' }
  | { type: 'HYDRATE_FROM_STORAGE'; payload: DegreeFilterHydrationPayload };

export const createInitialDegreeFilterState = (): DegreeFilterReducerState => ({
  selectedProductVariant: '',
  selectedDomain: '',
  selectedApplicationOpen: '',
  selectedCost: '',
  selectedDuration: '',
  selectedOpenContentAvailability: '',
  submittedProductVariant: '',
  submittedDomain: '',
  submittedApplicationOpen: '',
  submittedCostBucket: '',
  submittedDurationBucket: '',
  submittedOpenContentAvailabilityBucket: '',
  open: false,
  startupComplete: false,
});

/**
 * Mirrors the "selected" filter fields to the current "submitted" fields. Reproduces the
 * behavior of the resync `useEffect` that used to run whenever any submitted* field changed:
 * it always re-derives ALL selected fields from ALL submitted fields (not just the one that
 * changed), and preserves the pre-existing asymmetric `?? ''` fallback used only for
 * cost/duration.
 */
const resyncSelectedFromSubmitted = (state: DegreeFilterReducerState): DegreeFilterReducerState => ({
  ...state,
  selectedDomain: state.submittedDomain,
  selectedProductVariant: state.submittedProductVariant,
  selectedApplicationOpen: state.submittedApplicationOpen,
  selectedCost: state.submittedCostBucket ?? '',
  selectedDuration: state.submittedDurationBucket ?? '',
  selectedOpenContentAvailability: state.submittedOpenContentAvailabilityBucket,
});

// All fields on DegreeFilterReducerState are primitives, so a shallow key comparison is
// sufficient to detect a genuine no-op transition.
const isSameState = (a: DegreeFilterReducerState, b: DegreeFilterReducerState): boolean =>
  (Object.keys(a) as Array<keyof DegreeFilterReducerState>).every((key) => a[key] === b[key]);

const computeNextDegreeFilterState = (
  state: DegreeFilterReducerState,
  action: DegreeFilterAction
): DegreeFilterReducerState => {
  switch (action.type) {
    case 'SET_SELECTED_PRODUCT_VARIANT':
      return { ...state, selectedProductVariant: action.payload };
    case 'SET_SELECTED_DOMAIN':
      return { ...state, selectedDomain: action.payload };
    case 'SET_SELECTED_APPLICATION_OPEN':
      return { ...state, selectedApplicationOpen: action.payload };
    case 'SET_SELECTED_COST':
      return { ...state, selectedCost: action.payload };
    case 'SET_SELECTED_DURATION':
      return { ...state, selectedDuration: action.payload };
    case 'SET_SELECTED_OPEN_CONTENT_AVAILABILITY':
      return { ...state, selectedOpenContentAvailability: action.payload };
    case 'SET_SUBMITTED_PRODUCT_VARIANT':
      return resyncSelectedFromSubmitted({ ...state, submittedProductVariant: action.payload });
    case 'SET_SUBMITTED_DOMAIN':
      return resyncSelectedFromSubmitted({ ...state, submittedDomain: action.payload });
    case 'SET_SUBMITTED_APPLICATION_OPEN':
      return resyncSelectedFromSubmitted({ ...state, submittedApplicationOpen: action.payload });
    case 'SET_SUBMITTED_COST_BUCKET':
      return resyncSelectedFromSubmitted({ ...state, submittedCostBucket: action.payload });
    case 'SET_SUBMITTED_DURATION_BUCKET':
      return resyncSelectedFromSubmitted({ ...state, submittedDurationBucket: action.payload });
    case 'SET_SUBMITTED_OPEN_CONTENT_AVAILABILITY_BUCKET':
      return resyncSelectedFromSubmitted({ ...state, submittedOpenContentAvailabilityBucket: action.payload });
    case 'SET_OPEN':
      return { ...state, open: action.payload };
    case 'CLEAR_ALL':
      return resyncSelectedFromSubmitted({
        ...state,
        submittedDomain: '',
        submittedProductVariant: '',
        submittedApplicationOpen: '',
        submittedCostBucket: '',
        submittedDurationBucket: '',
        submittedOpenContentAvailabilityBucket: '',
      });
    case 'HYDRATE_FROM_STORAGE': {
      const prevState = action.payload;
      if (!prevState) {
        return { ...state, startupComplete: true };
      }
      const { domain, productVariant, applicationOpen, costBucket, durationBucket, openContentAvailabilityBucket } =
        prevState;
      const hydrated: DegreeFilterReducerState = {
        ...state,
        submittedDomain: domain || state.submittedDomain,
        submittedProductVariant: productVariant || state.submittedProductVariant,
        submittedApplicationOpen: applicationOpen || state.submittedApplicationOpen,
        submittedCostBucket: costBucket || state.submittedCostBucket,
        submittedDurationBucket: durationBucket || state.submittedDurationBucket,
        submittedOpenContentAvailabilityBucket:
          openContentAvailabilityBucket || state.submittedOpenContentAvailabilityBucket,
      };
      return { ...resyncSelectedFromSubmitted(hydrated), startupComplete: true };
    }
    default:
      return state;
  }
};

/**
 * `useReducer` (unlike `useState`) does not bail out of re-rendering when a dispatch produces a
 * state that's value-equal to the current one -- every case above returns a new object via
 * spread. Several `useEffect`s in the consuming hook dispatch on every render when one of their
 * OTHER dependencies (unrelated to this reducer) happens to get a new reference each render, so
 * without an explicit no-op bailout here, a same-value dispatch would trigger a re-render, which
 * re-runs those effects, which dispatch again -- an infinite loop. Returning the *same* `state`
 * reference for a no-op transition restores the bailout `useState` gave for free.
 */
export const degreeFilterReducer = (
  state: DegreeFilterReducerState,
  action: DegreeFilterAction
): DegreeFilterReducerState => {
  const nextState = computeNextDegreeFilterState(state, action);
  return isSameState(state, nextState) ? state : nextState;
};
